import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Bot,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  SlidersHorizontal,
  X,
  Paperclip,
  Film,
  Wrench,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import SimpleMarkdown from "@/components/SimpleMarkdown";

/**
 * Asistente IA de anuncios (Google Ads y Meta Ads, superadmin). La IA lee datos
 * reales y PROPONE cambios; cada propuesta es una tarjeta que solo se ejecuta
 * al pulsar "Aplicar" (el backend re-valida y aplica las guardas de la cuenta).
 * En Meta además se suben fotos/vídeos y se ven las imágenes generadas.
 */

export interface AssistantPlatformConfig {
  /** Base de la API del asistente, p. ej. "/admin/meta-ads/ai". */
  apiBase: string;
  title: string;
  /** Nombre de la plataforma tal como se enseña ("Aplicado en Meta Ads"). */
  platformLabel: string;
  storageKey: string;
  suggestions: string[];
  /** Base de subida de material (solo Meta), p. ej. "/admin/meta-ads/media". */
  mediaBase?: string;
}

interface MediaRef {
  kind: "image" | "video";
  name: string;
  hash?: string;
  videoId?: string;
  url: string | null;
}

export interface ProposedAction {
  id: string;
  type: string;
  summary: string;
  reason: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
  executedAt: string | null;
  errorMessage: string | null;
}

interface ChatEntry {
  role: "user" | "assistant";
  content: string;
  actions?: ProposedAction[];
  media?: MediaRef[];
  error?: boolean;
}

interface ChatResponse {
  reply: string;
  proposedActions: ProposedAction[];
  generatedImages?: Array<{ hash: string; url: string | null }>;
  toolsUsed: string[];
  usage: { inputTokens: number; outputTokens: number };
  model: string;
}

/** Cambios que empiezan a gastar o sube el gasto: confirmación extra. */
const RISKY_SUMMARY = /^(ACTIVAR|Cambiar el presupuesto|Reactivar)/;

const STATUS_FOR_MODEL: Record<string, string> = {
  pending: "pendiente de decisión",
  executing: "aplicándose",
  executed: "APLICADA por el usuario",
  failed: "falló al aplicar",
  discarded: "descartada por el usuario",
};

/** El modelo solo ve texto: se le cuenta qué pasó con sus propuestas. */
function contentForModel(entry: ChatEntry): string {
  const mediaLines = (entry.media ?? []).map((m) =>
    m.kind === "image"
      ? `[${entry.role === "user" ? "Archivo subido" : "Imagen generada"}: imagen "${m.name}", imageHash=${m.hash}]`
      : `[Archivo subido: vídeo "${m.name}", videoId=${m.videoId}]`,
  );
  const withMedia = mediaLines.length ? `${entry.content}\n${mediaLines.join("\n")}` : entry.content;
  if (!entry.actions?.length) return withMedia;
  const lines = entry.actions.map((a) => {
    const expired = a.status === "pending" && new Date(a.expiresAt).getTime() < Date.now();
    const status = expired ? "caducada sin aplicar" : (STATUS_FOR_MODEL[a.status] ?? a.status);
    return `- ${a.summary} → ${status}${a.errorMessage ? ` (${a.errorMessage})` : ""}`;
  });
  return `${withMedia}\n\n[Estado de mis propuestas]\n${lines.join("\n")}`;
}


function loadHistory(storageKey: string): ChatEntry[] {
  try {
    const raw = sessionStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as ChatEntry[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(storageKey: string, entries: ChatEntry[]): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(entries.slice(-60)));
  } catch {
    // Sin almacenamiento disponible: el chat sigue funcionando en memoria.
  }
}

function ActionStatus({ action, platformLabel }: { action: ProposedAction; platformLabel: string }) {
  const expired = action.status === "pending" && new Date(action.expiresAt).getTime() < Date.now();
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Clock className="w-3.5 h-3.5" /> Caducada
      </span>
    );
  }
  const map: Record<string, { label: string; className: string; icon: ReactNode }> = {
    executed: {
      label: `Aplicado en ${platformLabel}`,
      className: "text-emerald-700",
      icon: <CheckCircle className="w-3.5 h-3.5" />,
    },
    failed: { label: "Falló", className: "text-red-600", icon: <XCircle className="w-3.5 h-3.5" /> },
    discarded: { label: "Descartada", className: "text-gray-500", icon: <XCircle className="w-3.5 h-3.5" /> },
    executing: {
      label: "Aplicando…",
      className: "text-primary-700",
      icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
    },
  };
  const entry = map[action.status];
  if (!entry) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${entry.className}`}>
      {entry.icon} {entry.label}
    </span>
  );
}

function ActionCard({
  action,
  platformLabel,
  busy,
  fixing,
  onApply,
  onDiscard,
  onFix,
}: {
  action: ProposedAction;
  platformLabel: string;
  busy: boolean;
  /** Ya hay un mensaje en curso: no se puede pedir otro arreglo mientras tanto. */
  fixing: boolean;
  onApply: (action: ProposedAction) => void;
  onDiscard: (action: ProposedAction) => void;
  onFix: (action: ProposedAction) => void;
}) {
  const pending = action.status === "pending" && new Date(action.expiresAt).getTime() >= Date.now();
  const failed = action.status === "failed";
  return (
    <div className="border border-primary-200 bg-primary-50/60 rounded-xl p-3 text-sm">
      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-1">Cambio propuesto</p>
      <p className="text-gray-900">{action.summary}</p>
      {action.reason && <p className="text-xs text-gray-500 mt-1">Motivo: {action.reason}</p>}
      {action.errorMessage && <p className="text-xs text-red-600 mt-1">{action.errorMessage}</p>}
      <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
        <ActionStatus action={action} platformLabel={platformLabel} />
        {failed && (
          <button
            onClick={() => onFix(action)}
            disabled={fixing}
            title={`Le paso el error de ${platformLabel} a la IA para que prepare una propuesta corregida`}
            className="inline-flex items-center gap-1.5 ml-auto px-3 py-1.5 text-xs font-medium text-primary-700 border border-primary-200 bg-white rounded-lg hover:bg-primary-50 disabled:opacity-50"
          >
            <Wrench className="w-3.5 h-3.5" /> Arreglarlo con la IA
          </button>
        )}
        {pending && (
          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => onDiscard(action)}
              disabled={busy}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Descartar
            </button>
            <button
              onClick={() => onApply(action)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Aplicar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const MAX_NOTES = 4000;

function MediaThumbs({ media }: { media: MediaRef[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {media.map((m, index) =>
        m.kind === "image" && m.url ? (
          <a key={index} href={m.url} target="_blank" rel="noreferrer" className="block">
            <img src={m.url} alt={m.name} className="h-28 w-28 object-cover rounded-lg border border-gray-200 bg-white" />
          </a>
        ) : (
          <span key={index} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-600">
            <Film className="w-3.5 h-3.5" /> {m.name}
          </span>
        ),
      )}
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}

function InstructionsModal({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const { showToast } = useGlobalToast();
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get<{ success: true; data: { businessNotes: string } }>(`${apiBase}/settings`)
      .then(({ data }) => setNotes(data.data.businessNotes))
      .catch((err: unknown) => showToast(extractApiErrorMessage(err, "No se pudieron cargar las indicaciones"), "error"))
      .finally(() => setLoading(false));
  }, [apiBase, showToast]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put<{ success: true; message: string }>(`${apiBase}/settings`, {
        businessNotes: notes,
      });
      showToast(data.message, "success");
      onClose();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudieron guardar"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 sm:p-6 max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Indicaciones para la IA</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400" title="Cerrar">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-600">
          La IA ya trabaja con un enfoque B2B estricto: solo dueños y responsables de restaurantes y negocios con cita que
          buscan un software, nunca clientes finales. Aquí puedes añadir lo que quieras priorizar ahora mismo; lo usan el
          asistente y los informes.
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Ejemplos: "Este mes priorizamos restaurantes en España", "No propongas subir presupuestos hasta octubre", "Las
          clínicas no son nuestro cliente".
        </p>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
          disabled={loading}
          rows={8}
          className="mt-3 w-full flex-1 min-h-[140px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
          placeholder={loading ? "Cargando…" : "Escribe tus indicaciones…"}
        />
        <p className="text-xs text-gray-400 mt-1 text-right">
          {notes.length}/{MAX_NOTES}
        </p>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || loading}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdsAssistant({
  config,
  draft,
  onDraftConsumed,
  onApplied,
}: {
  config: AssistantPlatformConfig;
  draft?: string | null;
  onDraftConsumed?: () => void;
  onApplied?: () => void;
}) {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const [entries, setEntries] = useState<ChatEntry[]>(() => loadHistory(config.storageKey));
  const [pendingMedia, setPendingMedia] = useState<MediaRef[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [busyActionId, setBusyActionId] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    saveHistory(config.storageKey, entries);
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [entries, config.storageKey]);

  useEffect(() => {
    if (draft) {
      setInput(draft);
      onDraftConsumed?.();
      inputRef.current?.focus();
    }
  }, [draft, onDraftConsumed]);

  const updateAction = useCallback((updated: ProposedAction) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.actions?.some((a) => a.id === updated.id)
          ? { ...entry, actions: entry.actions.map((a) => (a.id === updated.id ? updated : a)) }
          : entry,
      ),
    );
  }, []);

  const send = useCallback(
    async (text: string) => {
      const media = pendingMedia;
      const content = text.trim() || (media.length ? "Te he subido este material." : "");
      if (!content || sending || uploading) return;
      const userEntry: ChatEntry = { role: "user", content, ...(media.length ? { media } : {}) };
      const history = [...entries.filter((e) => !e.error), userEntry];
      setEntries((prev) => [...prev, userEntry]);
      setInput("");
      setPendingMedia([]);
      setSending(true);
      try {
        const { data } = await apiClient.post<{ success: true; data: ChatResponse }>(`${config.apiBase}/chat`, {
          messages: history.map((e) => ({ role: e.role, content: contentForModel(e) })),
        });
        const generated: MediaRef[] = (data.data.generatedImages ?? []).map((image, index) => ({
          kind: "image",
          name: `imagen-ia-${index + 1}`,
          hash: image.hash,
          url: image.url,
        }));
        setEntries((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.data.reply,
            actions: data.data.proposedActions,
            ...(generated.length ? { media: generated } : {}),
          },
        ]);
      } catch (err) {
        setEntries((prev) => [
          ...prev,
          {
            role: "assistant",
            content: extractApiErrorMessage(err, "El asistente no ha podido responder."),
            error: true,
          },
        ]);
      } finally {
        setSending(false);
      }
    },
    [entries, sending, uploading, pendingMedia, config.apiBase],
  );

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || !config.mediaBase) return;
      setUploading(true);
      try {
        for (const file of Array.from(files).slice(0, 5)) {
          if (file.type.startsWith("image/")) {
            if (file.size > 8 * 1024 * 1024) throw new Error(`"${file.name}" supera 8 MB`);
            const base64 = await readAsDataUrl(file);
            const { data } = await apiClient.post<{ success: true; data: { hash: string; url: string | null } }>(
              `${config.mediaBase}/image`,
              { base64, fileName: file.name },
            );
            setPendingMedia((prev) => [...prev, { kind: "image", name: file.name, hash: data.data.hash, url: data.data.url }]);
          } else if (file.type.startsWith("video/")) {
            if (file.size > 200 * 1024 * 1024) throw new Error(`"${file.name}" supera 200 MB`);
            const { data } = await apiClient.post<{ success: true; data: { videoId: string } }>(`${config.mediaBase}/video`, file, {
              headers: { "Content-Type": file.type, "X-File-Name": encodeURIComponent(file.name) },
              timeout: 15 * 60 * 1000,
            });
            setPendingMedia((prev) => [...prev, { kind: "video", name: file.name, videoId: data.data.videoId, url: null }]);
          } else {
            throw new Error(`"${file.name}" no es una imagen ni un vídeo`);
          }
        }
      } catch (err) {
        showToast(extractApiErrorMessage(err, err instanceof Error ? err.message : "No se pudo subir el archivo"), "error");
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    },
    [config.mediaBase, showToast],
  );

  const handleApply = useCallback(
    async (action: ProposedAction) => {
      if (RISKY_SUMMARY.test(action.summary)) {
        const ok = await confirm({
          title: "¿Aplicar este cambio?",
          message: action.summary,
          confirmText: "Sí, aplicar",
          danger: true,
        });
        if (!ok) return;
      }
      setBusyActionId(action.id);
      try {
        const { data } = await apiClient.post<{ success: true; message: string; data: ProposedAction }>(
          `${config.apiBase}/actions/${action.id}/confirm`,
        );
        updateAction(data.data);
        showToast(data.message, "success");
        onApplied?.();
      } catch (err) {
        const updated = (err as { response?: { data?: { data?: ProposedAction | null } } })?.response?.data?.data;
        if (updated) updateAction(updated);
        showToast(extractApiErrorMessage(err, "No se pudo aplicar el cambio"), "error");
      } finally {
        setBusyActionId(null);
      }
    },
    [confirm, showToast, updateAction, onApplied, config.apiBase],
  );

  const handleDiscard = useCallback(
    async (action: ProposedAction) => {
      setBusyActionId(action.id);
      try {
        const { data } = await apiClient.post<{ success: true; data: ProposedAction }>(
          `${config.apiBase}/actions/${action.id}/discard`,
        );
        updateAction(data.data);
      } catch (err) {
        showToast(extractApiErrorMessage(err, "No se pudo descartar"), "error");
      } finally {
        setBusyActionId(null);
      }
    },
    [showToast, updateAction, config.apiBase],
  );

  /**
   * Una propuesta ha fallado al aplicarla (Meta/Google la rechazó): en vez de
   * obligar a Luis a copiar el error, se le pasa a la IA en el mismo chat para
   * que prepare una propuesta corregida. El historial ya lleva el estado y el
   * error de cada propuesta, así que el modelo tiene el contexto completo.
   */
  const handleFix = useCallback(
    (action: ProposedAction) => {
      const error = action.errorMessage ?? "sin detalle del error";
      void send(
        `La propuesta «${action.summary}» ha fallado al aplicarla. Error de ${config.platformLabel}: «${error}». ` +
          "Diagnostica el motivo, corrígelo manteniendo la intención y prepárame una propuesta nueva que sí se pueda aplicar; dime qué has cambiado.",
      );
    },
    [send, config.platformLabel],
  );

  const handleClear = useCallback(async () => {
    const ok = await confirm({
      title: "¿Empezar una conversación nueva?",
      message: "Se borra el historial de este chat en este navegador. Los cambios aplicados no se deshacen.",
      confirmText: "Nueva conversación",
    });
    if (ok) setEntries([]);
  }, [confirm]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col h-[calc(100dvh-18rem)] min-h-[460px]">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="w-5 h-5 text-primary-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{config.title}</p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">Lee datos reales. Ningún cambio se aplica sin tu clic.</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setShowInstructions(true)}
            title="Indicaciones para la IA"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 border border-gray-200 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Indicaciones</span>
          </button>
          {entries.length > 0 && (
            <button
              onClick={() => void handleClear()}
              title="Nueva conversación"
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {entries.length === 0 && (
          <div className="max-w-xl mx-auto text-center py-6">
            <p className="text-sm text-gray-600">
              Pregunta por el rendimiento, pide análisis o propuestas de cambios. Algunos ejemplos:
            </p>
            <div className="mt-4 grid gap-2">
              {config.suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => void send(suggestion)}
                  className="text-left text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {entries.map((entry, index) =>
          entry.role === "user" ? (
            <div key={index} className="flex justify-end">
              <div className="max-w-[85%] space-y-2 flex flex-col items-end">
                <div className="bg-primary-600 text-white rounded-2xl rounded-br-md px-3.5 py-2 text-sm whitespace-pre-wrap break-words">
                  {entry.content}
                </div>
                {entry.media?.length ? <MediaThumbs media={entry.media} /> : null}
              </div>
            </div>
          ) : (
            <div key={index} className="flex justify-start">
              <div className="max-w-[92%] space-y-2">
                <div
                  className={`rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm break-words ${
                    entry.error ? "bg-red-50 text-red-700 border border-red-100" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {entry.error ? (
                    <p className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {entry.content}
                    </p>
                  ) : (
                    <SimpleMarkdown text={entry.content} />
                  )}
                </div>
                {entry.media?.length ? <MediaThumbs media={entry.media} /> : null}
                {entry.actions?.map((action) => (
                  <ActionCard
                    key={action.id}
                    action={action}
                    platformLabel={config.platformLabel}
                    busy={busyActionId === action.id}
                    fixing={sending || uploading}
                    onApply={(a) => void handleApply(a)}
                    onDiscard={(a) => void handleDiscard(a)}
                    onFix={handleFix}
                  />
                ))}
              </div>
            </div>
          ),
        )}

        {sending && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Consultando la cuenta y pensando…
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
        className="border-t border-gray-100 p-3 pr-20 flex flex-col gap-2"
      >
        {pendingMedia.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <MediaThumbs media={pendingMedia} />
            <button
              type="button"
              onClick={() => setPendingMedia([])}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Quitar
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          {config.mediaBase && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime,video/webm"
                multiple
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || sending}
                title="Subir fotos o vídeos para anuncios"
                className="p-2.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
            </>
          )}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
            rows={Math.min(6, Math.max(2, input.split("\n").length, Math.ceil(input.length / 70)))}
            placeholder="Escribe tu pregunta o lo que quieres cambiar…"
            className="flex-1 resize-none px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={sending || uploading || (!input.trim() && pendingMedia.length === 0)}
            className="p-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            title="Enviar"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </form>
      {showInstructions && <InstructionsModal apiBase={config.apiBase} onClose={() => setShowInstructions(false)} />}
    </div>
  );
}
